import React from "react";
import {
  CheckCircle2,
  X
} from "lucide-react";
import "./CarrierSignupForm.css";

const equipmentOptions = [
  { id: "dry-van", label: "Dry Van" },
  { id: "reefer", label: "Reefer" },
  { id: "flatbed", label: "Flatbed" },
  { id: "step-deck", label: "Step Deck" },
  { id: "other-equip", label: "Other" }
];

const CarrierSignupForm = ({ open, onClose }) => {
  const [status, setStatus] = React.useState({ type: "", message: "" });
  const [loading, setLoading] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [debugInfo, setDebugInfo] = React.useState("");
  const [formData, setFormData] = React.useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    mcNumber: "",
    dotNumber: "",
    equipment: [],
    message: ""
  });

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && open) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const resetForm = () => {
    setFormData({
      name: "",
      company: "",
      email: "",
      phone: "",
      mcNumber: "",
      dotNumber: "",
      equipment: [],
      message: ""
    });
    setStatus({ type: "", message: "" });
    setShowSuccess(false);
    setDebugInfo("");
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetForm, 300);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEquipmentChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      equipment: checked
        ? [...prev.equipment, value]
        : prev.equipment.filter((item) => item !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        FormType: "Carrier Signup",
        _subject: "New Carrier Signup",
        _replyto: formData.email,
        Name: formData.name,
        Company: formData.company,
        Email: formData.email,
        Phone: formData.phone,
        MCNumber: formData.mcNumber,
        DOTNumber: formData.dotNumber,
        Equipment: formData.equipment.join(", "),
        Message: formData.message,
        Source: window.location.href,
        SubmittedAt: new Date().toISOString(),
        RawFormData: JSON.stringify(formData)
      };

      const formBody = new URLSearchParams();
      Object.entries(payload).forEach(([key, value]) => {
        formBody.append(key, value ?? "");
      });

      const response = await fetch("https://formspree.io/f/mgolkdry", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json"
        },
        body: formBody.toString()
      });

      let responseData = null;
      try {
        responseData = await response.json();
      } catch (parseError) {
        responseData = null;
      }

      if (response.ok && (!responseData || responseData.ok !== false)) {
        setStatus({ type: "success", message: "" });
        setShowSuccess(true);
        setDebugInfo(`Formspree OK (${response.status})`);
      } else {
        const message = responseData?.error || responseData?.message || "Failed to submit form";
        console.error("Formspree error (carrier):", response.status, responseData);
        setDebugInfo(`Formspree error (${response.status}): ${JSON.stringify(responseData)}`);
        throw new Error(message);
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: "There was an error submitting your request. Please try again."
      });
      setDebugInfo(`Submit error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className={`carrier-modal-overlay ${open ? "active" : ""}`}
      onClick={(e) => {
        if (e.target.classList.contains("carrier-modal-overlay")) {
          handleClose();
        }
      }}
    >
      <div className="carrier-modal">
        <div className="carrier-modal-header">
          <div className="carrier-modal-header-content">
            <h2>Join Our Carrier Network</h2>
            <p>Start hauling with Sunny Logistics</p>
          </div>
          <button className="carrier-modal-close" type="button" onClick={handleClose}>
            <X />
          </button>
        </div>

        {!showSuccess && (
          <div className="carrier-form-state">
            <div className="carrier-modal-body">
              <div className="carrier-quick-benefits">
                <div className="carrier-quick-benefit">
                  <CheckCircle2 />
                  24hr Quick Pay
                </div>
                <div className="carrier-quick-benefit">
                  <CheckCircle2 />
                  Dedicated Rep
                </div>
                <div className="carrier-quick-benefit">
                  <CheckCircle2 />
                  No Surprises
                </div>
              </div>

              {status.type === "error" && (
                <div className="carrier-form-error">
                  {status.message}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="carrier-form-row">
                  <div className="carrier-form-group">
                    <label className="carrier-form-label">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      className="carrier-form-input"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div className="carrier-form-group">
                    <label className="carrier-form-label">
                      Company Name <span className="required">*</span>
                    </label>
                    <input
                      className="carrier-form-input"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Smith Trucking LLC"
                      required
                    />
                  </div>
                </div>

                <div className="carrier-form-row">
                  <div className="carrier-form-group">
                    <label className="carrier-form-label">
                      Email <span className="required">*</span>
                    </label>
                    <input
                      className="carrier-form-input"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  <div className="carrier-form-group">
                    <label className="carrier-form-label">
                      Phone <span className="required">*</span>
                    </label>
                    <input
                      className="carrier-form-input"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(317) 555-1234"
                      required
                    />
                  </div>
                </div>

                <div className="carrier-form-row">
                  <div className="carrier-form-group">
                    <label className="carrier-form-label">MC#</label>
                    <input
                      className="carrier-form-input"
                      name="mcNumber"
                      value={formData.mcNumber}
                      onChange={handleChange}
                      placeholder="MC-123456"
                    />
                  </div>
                  <div className="carrier-form-group">
                    <label className="carrier-form-label">DOT#</label>
                    <input
                      className="carrier-form-input"
                      name="dotNumber"
                      value={formData.dotNumber}
                      onChange={handleChange}
                      placeholder="1234567"
                    />
                  </div>
                </div>

                <label className="carrier-equipment-label">Equipment Type</label>
                <div className="carrier-equipment-options">
                  {equipmentOptions.map((option) => (
                    <div className="carrier-equipment-option" key={option.id}>
                      <input
                        id={option.id}
                        type="checkbox"
                        value={option.label}
                        checked={formData.equipment.includes(option.label)}
                        onChange={handleEquipmentChange}
                      />
                      <label htmlFor={option.id}>{option.label}</label>
                    </div>
                  ))}
                </div>

                <div className="carrier-form-group carrier-form-group-full">
                  <label className="carrier-form-label">
                    Message <span className="carrier-optional">(Optional)</span>
                  </label>
                  <textarea
                    className="carrier-form-input carrier-textarea"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Preferred lanes, fleet size, or anything else..."
                  />
                </div>

                <button type="submit" className="carrier-submit-btn" disabled={loading}>
                  {loading ? "Submitting..." : "Submit Application →"}
                </button>
                {debugInfo && (
                  <p className="carrier-submit-note">Debug: {debugInfo}</p>
                )}
                <div className="carrier-submit-note">
                  <CheckCircle2 />
                  We respond within 1 business day
                </div>
              </form>
            </div>

            <div className="carrier-modal-footer">
              <p>
                Prefer to call? <a href="tel:+13175370657">(317) 537-0657</a>
              </p>
            </div>
          </div>
        )}

        {showSuccess && (
          <div className="carrier-success-state">
            <div className="carrier-success-icon">
              <CheckCircle2 />
            </div>
            <h3>Application Received!</h3>
            <p>
              Thanks for your interest in hauling with Sunny Logistics. We'll review your info and reach out within 1 business day.
            </p>
            <button type="button" className="carrier-success-btn" onClick={handleClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarrierSignupForm;

